// Tagesliste — kompakte Listenansicht aller Termine eines Tages (Alternative
// zum Raster). Teil der Spezifikation "Zwei Kalenderansichten". Zeilen öffnen
// per Klick die Bearbeitung, per Rechtsklick das Mandanten-Kontextmenü.
import React, { useMemo, useState } from 'react';
import { Search, CheckCircle, Clock, User } from 'lucide-react';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const fmtTime = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const statusOf = (apt) => {
  if (apt.billing_status === 'billed' || apt.billing_status === 'paid') return 'billed';
  if (apt.checked_in) return 'checked_in';
  return 'open';
};

const STATUS_BADGE = {
  open: { label: 'Noch nicht eingecheckt', cls: 'bg-gray-100 text-gray-600' },
  checked_in: { label: 'Eingecheckt', cls: 'bg-green-100 text-green-700' },
  billed: { label: 'Abgerechnet', cls: 'bg-blue-100 text-blue-700' },
};

export const DayListView = ({
  appointments, currentDate, serviceMap, trainers,
  getAppointmentColor, onAppointmentClick, onRightClick, onQuickCheckIn,
}) => {
  const [search, setSearch] = useState('');
  const [trainerFilter, setTrainerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const trainerNameById = useMemo(() => {
    const m = {};
    (trainers || []).forEach((td) => {
      const t = td.trainer || td;
      m[t.id] = t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim();
    });
    return m;
  }, [trainers]);

  const dayAppointments = useMemo(() => {
    const dateKey = currentDate.toDateString();
    return (appointments || [])
      .filter((apt) => new Date(apt.start_time).toDateString() === dateKey)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  }, [appointments, currentDate]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dayAppointments.filter((apt) => {
      if (trainerFilter !== 'all' && apt.trainer_id !== trainerFilter) return false;
      if (statusFilter !== 'all' && statusOf(apt) !== statusFilter) return false;
      if (!q) return true;
      const names = (apt.customer_names || [apt.customer_name]).filter(Boolean).join(' ').toLowerCase();
      const svc = (serviceMap[apt.service_id]?.name || apt.service_name || '').toLowerCase();
      return names.includes(q) || svc.includes(q);
    });
  }, [dayAppointments, trainerFilter, statusFilter, search, serviceMap]);

  return (
    <div className="p-4" data-testid="day-list-view">
      {/* Filterleiste */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kunde oder Leistung suchen …"
            className="pl-9"
            data-testid="day-list-search"
          />
        </div>
        <Select value={trainerFilter} onValueChange={setTrainerFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="day-list-trainer-filter">
            <SelectValue placeholder="Mitarbeiter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Mitarbeiter</SelectItem>
            {(trainers || []).map((td) => {
              const t = td.trainer || td;
              return <SelectItem key={t.id} value={t.id}>{trainerNameById[t.id] || 'Mitarbeiter'}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="day-list-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="open">Noch nicht eingecheckt</SelectItem>
            <SelectItem value="checked_in">Eingecheckt</SelectItem>
            <SelectItem value="billed">Abgerechnet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="day-list-empty">
          Keine Termine für diesen Tag
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden">
          {rows.map((apt) => {
            const st = statusOf(apt);
            const badge = STATUS_BADGE[st];
            const names = (apt.customer_names || [apt.customer_name]).filter(Boolean);
            const svcName = serviceMap[apt.service_id]?.name || apt.service_name || 'Termin';
            const color = getAppointmentColor ? getAppointmentColor(apt) : '#14b8a6';
            const clickedCustomer = apt.customer_ids?.[0] || apt.customer_id || null;
            return (
              <li
                key={apt.id}
                className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onAppointmentClick(apt)}
                onContextMenu={(e) => onRightClick(e, apt, clickedCustomer)}
                data-testid={`day-list-row-${apt.id}`}
              >
                <span className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 w-14 flex-shrink-0">
                  <Clock size={13} className="text-gray-400" />
                  {fmtTime(apt.start_time)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {names.length ? names.join(', ') : 'Ohne Kunde'}
                  </div>
                  <div className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                    <span>{svcName}</span>
                    {apt.trainer_id && trainerNameById[apt.trainer_id] && (
                      <span className="inline-flex items-center gap-0.5">· <User size={11} />{trainerNameById[apt.trainer_id]}</span>
                    )}
                  </div>
                  {(apt.customer_daylist_fields?.[0] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`day-list-fields-${apt.id}`}>
                      {apt.customer_daylist_fields[0].map((cf, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">
                          <span className="opacity-70">{cf.label}:</span> {cf.value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className={`hidden sm:inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${badge.cls}`} data-testid={`day-list-status-${apt.id}`}>
                  {badge.label}
                </span>
                {st === 'open' && onQuickCheckIn && (
                  <button
                    className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 flex-shrink-0"
                    onClick={(e) => { e.stopPropagation(); onQuickCheckIn(apt); }}
                    data-testid={`day-list-checkin-${apt.id}`}
                  >
                    <CheckCircle size={13} />
                    Check-in
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DayListView;
